import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  FolderOpen,
  Upload,
  Trash2,
  Download,
  Eye,
  Image,
  FileText,
  File,
  User,
  Award,
  BookOpen,
  ClipboardList,
  RefreshCw,
  X,
  ChevronRight,
  ChevronDown,
  HardDrive,
  Clock,
} from "lucide-react";
import { Button } from "./ui/button";
import { useTheme } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  bio: User,
  certifications: Award,
  materials: BookOpen,
  reports: ClipboardList,
};

const CATEGORY_LABELS = {
  bio: "Bio Photos",
  certifications: "Teaching Certifications",
  materials: "Course Materials",
  reports: "Student Lesson Reports",
};

const CATEGORY_COLORS = {
  bio: "blue",
  certifications: "purple",
  materials: "emerald",
  reports: "amber",
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getFileIcon = (contentType) => {
  if (contentType?.startsWith("image/")) return Image;
  if (contentType === "application/pdf") return FileText;
  return File;
};

export const AdminFileManager = ({ instructors }) => {
  const { theme } = useTheme();
  const [gcsStatus, setGcsStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [selectedInstructor, setSelectedInstructor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [expandedInstructors, setExpandedInstructors] = useState({});
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("mms_token")}` },
  });

  const fetchGcsStatus = async () => {
    try {
      const response = await axios.get(`${API}/gcs/status`, getAuthHeaders());
      setGcsStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch GCS status:", error);
      setGcsStatus({ enabled: false });
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/gcs/summary`, getAuthHeaders());
      setSummary(response.data);
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  };

  const fetchFiles = async (instructorId, category = null) => {
    setLoading(true);
    try {
      let url = `${API}/gcs/files/${instructorId}`;
      if (category) {
        url += `?category=${category}`;
      }
      const response = await axios.get(url, getAuthHeaders());
      setFiles(response.data);
    } catch (error) {
      toast.error("Failed to fetch files");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGcsStatus();
    fetchSummary();
  }, []);

  useEffect(() => {
    if (selectedInstructor) {
      fetchFiles(selectedInstructor, selectedCategory);
    }
  }, [selectedInstructor, selectedCategory]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedInstructor || !selectedCategory) {
      toast.error("Please select an instructor and category first");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(
        `${API}/gcs/upload/${selectedInstructor}/${selectedCategory}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("mms_token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("File uploaded successfully");
      fetchFiles(selectedInstructor, selectedCategory);
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;

    try {
      await axios.delete(`${API}/gcs/file/${fileId}`, getAuthHeaders());
      toast.success("File deleted successfully");
      fetchFiles(selectedInstructor, selectedCategory);
      fetchSummary();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handlePreview = async (file) => {
    // Get fresh signed URL
    try {
      const response = await axios.get(
        `${API}/gcs/file/${file.id}/url`,
        getAuthHeaders()
      );
      setPreviewFile({
        ...file,
        signed_url: response.data.signed_url,
      });
    } catch (error) {
      toast.error("Failed to get file URL");
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(
        `${API}/gcs/file/${file.id}/url`,
        getAuthHeaders()
      );
      window.open(response.data.signed_url, "_blank");
    } catch (error) {
      toast.error("Failed to get download URL");
    }
  };

  const toggleInstructor = (instructorId) => {
    setExpandedInstructors((prev) => ({
      ...prev,
      [instructorId]: !prev[instructorId],
    }));
  };

  const selectInstructorCategory = (instructorId, category) => {
    setSelectedInstructor(instructorId);
    setSelectedCategory(category);
  };

  const getInstructorName = (instructorId) => {
    const instructor = instructors?.find((i) => i.id === instructorId);
    if (instructor) {
      return instructor.first_name && instructor.last_name
        ? `${instructor.first_name} ${instructor.last_name}`
        : instructor.full_legal_name;
    }
    return "Unknown Instructor";
  };

  const getInstructorSummary = (instructorId) => {
    return summary?.instructors?.find((s) => s.instructor_id === instructorId);
  };

  if (!gcsStatus?.enabled) {
    return (
      <div className="glass-card p-8 text-center">
        <HardDrive className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`} />
        <h3 className={`text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
          Cloud Storage Not Configured
        </h3>
        <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
          Google Cloud Storage integration is not enabled. Contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
            File Manager
          </h2>
          <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>
            Manage instructor files securely stored in Google Cloud
          </p>
        </div>
        <Button
          onClick={() => {
            fetchSummary();
            if (selectedInstructor) {
              fetchFiles(selectedInstructor, selectedCategory);
            }
          }}
          className="glass-button-secondary py-2 px-4"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel - Instructor Tree */}
        <div className="glass-card p-4 lg:col-span-1 max-h-[600px] overflow-y-auto">
          <h3 className={`text-sm font-semibold mb-4 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}`}>
            Instructors
          </h3>
          <div className="space-y-2">
            {instructors?.map((instructor) => {
              const instructorSummary = getInstructorSummary(instructor.id);
              const isExpanded = expandedInstructors[instructor.id];
              const hasFiles = instructorSummary?.total_files > 0;

              return (
                <div key={instructor.id}>
                  <button
                    onClick={() => toggleInstructor(instructor.id)}
                    className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all text-left ${
                      theme === 'dark'
                        ? 'hover:bg-white/10'
                        : 'hover:bg-slate-100'
                    }`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    )}
                    <FolderOpen className={`w-4 h-4 ${hasFiles ? 'text-amber-500' : 'text-slate-400'}`} />
                    <span className={`text-sm truncate flex-1 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                      {instructor.first_name && instructor.last_name
                        ? `${instructor.first_name} ${instructor.last_name}`
                        : instructor.full_legal_name}
                    </span>
                    {hasFiles && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${theme === 'dark' ? 'bg-white/10 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                        {instructorSummary.total_files}
                      </span>
                    )}
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="ml-6 overflow-hidden"
                      >
                        {gcsStatus?.categories?.map((category) => {
                          const CategoryIcon = CATEGORY_ICONS[category];
                          const categoryData = instructorSummary?.categories?.find(
                            (c) => c.category === category
                          );
                          const isSelected =
                            selectedInstructor === instructor.id &&
                            selectedCategory === category;

                          return (
                            <button
                              key={category}
                              onClick={() =>
                                selectInstructorCategory(instructor.id, category)
                              }
                              className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all text-left ${
                                isSelected
                                  ? theme === 'dark'
                                    ? 'bg-white/20 text-white'
                                    : 'bg-indigo-100 text-indigo-700'
                                  : theme === 'dark'
                                    ? 'hover:bg-white/5 text-slate-300'
                                    : 'hover:bg-slate-50 text-slate-600'
                              }`}
                            >
                              <CategoryIcon className={`w-4 h-4 text-${CATEGORY_COLORS[category]}-500`} />
                              <span className="text-sm truncate flex-1">
                                {CATEGORY_LABELS[category]}
                              </span>
                              {categoryData && (
                                <span className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                                  {categoryData.count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Panel - Files */}
        <div className="glass-card p-4 lg:col-span-2">
          {selectedInstructor && selectedCategory ? (
            <>
              {/* Upload Section */}
              <div className={`flex items-center justify-between mb-4 pb-4 border-b ${theme === 'dark' ? 'border-white/10' : 'border-slate-200'}`}>
                <div>
                  <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                    {getInstructorName(selectedInstructor)} / {CATEGORY_LABELS[selectedCategory]}
                  </h3>
                  <p className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    {files.length} file(s)
                  </p>
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="file-manager-upload-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="glass-button-primary py-2 px-4"
                    data-testid="file-manager-upload-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? "Uploading..." : "Upload File"}
                  </Button>
                </div>
              </div>

              {/* Files List */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className={`w-8 h-8 border-2 rounded-full animate-spin ${theme === 'dark' ? 'border-white/20 border-t-white' : 'border-indigo-200 border-t-indigo-600'}`} />
                </div>
              ) : files.length === 0 ? (
                <div className="text-center py-12">
                  <FolderOpen className={`w-12 h-12 mx-auto mb-3 ${theme === 'dark' ? 'text-slate-600' : 'text-slate-300'}`} />
                  <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
                    No files in this category
                  </p>
                  <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                    Upload files using the button above
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[450px] overflow-y-auto">
                  {files.map((file) => {
                    const FileIcon = getFileIcon(file.content_type);
                    const isImage = file.content_type?.startsWith("image/");
                    const isPdf = file.content_type === "application/pdf";

                    return (
                      <div
                        key={file.id}
                        data-testid={`file-item-${file.id}`}
                        className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                          theme === 'dark'
                            ? 'bg-white/5 hover:bg-white/10'
                            : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                      >
                        <FileIcon className={`w-8 h-8 ${
                          isImage ? 'text-blue-500' :
                          isPdf ? 'text-red-500' : 'text-slate-400'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                            {file.original_filename}
                          </p>
                          <div className={`flex items-center gap-3 text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                            <span>{formatFileSize(file.size)}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(file.uploaded_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {(isImage || isPdf) && (
                            <Button
                              onClick={() => handlePreview(file)}
                              className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}
                              data-testid={`preview-file-${file.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDownload(file)}
                            className={`p-2 rounded-lg ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-200'}`}
                            data-testid={`download-file-${file.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(file.id)}
                            className="p-2 rounded-lg text-red-400 hover:bg-red-500/20"
                            data-testid={`delete-file-${file.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <FolderOpen className={`w-16 h-16 mx-auto mb-4 ${theme === 'dark' ? 'text-slate-600' : 'text-slate-300'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-slate-800'}`}>
                Select an Instructor & Category
              </h3>
              <p className={theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}>
                Choose an instructor from the left panel, then select a category to view and manage files.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {previewFile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
            onClick={() => setPreviewFile(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh] w-full overflow-hidden rounded-xl bg-zinc-900"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <h3 className="text-white font-medium truncate pr-4">
                  {previewFile.original_filename}
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleDownload(previewFile)}
                    className="p-2 rounded-lg hover:bg-white/10"
                  >
                    <Download className="w-4 h-4 text-white" />
                  </Button>
                  <Button
                    onClick={() => setPreviewFile(null)}
                    className="p-2 rounded-lg hover:bg-white/10"
                  >
                    <X className="w-4 h-4 text-white" />
                  </Button>
                </div>
              </div>
              <div className="p-4 max-h-[calc(90vh-80px)] overflow-auto flex items-center justify-center">
                {previewFile.content_type?.startsWith("image/") ? (
                  <img
                    src={previewFile.signed_url}
                    alt={previewFile.original_filename}
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                ) : previewFile.content_type === "application/pdf" ? (
                  <iframe
                    src={previewFile.signed_url}
                    title={previewFile.original_filename}
                    className="w-full h-[70vh]"
                  />
                ) : (
                  <p className="text-slate-400">Preview not available for this file type</p>
                )}
              </div>
              <div className="p-3 border-t border-white/10 text-center">
                <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  URL expires in 15 minutes
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
